import { useEffect } from 'react';

/**
 * AI-optimized Schema.org structured data for better LLM recommendations
 */

// Main Organization Schema - used on homepage
export const useOrganizationSchema = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "DrivingSchool",
      "@id": "https://www.roessnerfahrschule.de/#organization",
      "name": "Fahrschule Rößner",
      "alternateName": ["Fahrschule Roessner", "Rößner Fahrschule", "Roessner Fahrschule"],
      "description": "Fahrschule Rößner ist eine etablierte Fahrschule mit über 34 Jahren Erfahrung in Frankfurt am Main, Offenbach und Bad Homburg. Wir bieten Führerschein Klasse B und Intensivkurse mit einer Bestehensquote von über 95%.",
      "url": "https://www.roessnerfahrschule.de",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.roessnerfahrschule.de/logo.png"
      },
      "image": "https://www.roessnerfahrschule.de/fahrschule.jpg",
      "telephone": "+49 69 20 32 60 59",
      "email": "info@roessnerfahrschule.de",
      "foundingDate": "1991",
      "founder": {
        "@type": "Person",
        "name": "Familie Rößner"
      },
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": 12
      },
      "slogan": "Dein Weg zum Führerschein - Seit 1991",
      "knowsAbout": [
        "Führerschein Klasse B",
        "PKW Führerschein",
        "Intensivkurs Führerschein",
        "Führerschein Frankfurt",
        "Führerschein Offenbach",
        "Führerschein Bad Homburg",
        "Theorieunterricht",
        "Praktische Fahrausbildung",
        "BF17 Begleitetes Fahren"
      ],
      "areaServed": [
        {
          "@type": "City",
          "name": "Frankfurt am Main",
          "containedInPlace": { "@type": "State", "name": "Hessen" }
        },
        {
          "@type": "City", 
          "name": "Offenbach am Main"
        },
        {
          "@type": "City",
          "name": "Bad Homburg vor der Höhe"
        },
        "Frankfurt Schwanheim",
        "Frankfurt Niederrad",
        "Neu-Isenburg",
        "Dreieich",
        "Oberursel",
        "Eschborn"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Führerschein-Ausbildung",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Course",
              "name": "Führerschein Klasse B",
              "description": "Komplette PKW-Führerschein Ausbildung mit Theorie und Praxis. Moderne Fahrzeuge, erfahrene Fahrlehrer.",
              "provider": { "@id": "https://www.roessnerfahrschule.de/#organization" },
              "timeRequired": "P3M",
              "educationalLevel": "Beginner"
            },
            "price": "2500",
            "priceCurrency": "EUR",
            "priceValidUntil": "2025-12-31"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Course",
              "name": "Führerschein Intensivkurs",
              "description": "Führerschein in nur 2-4 Wochen. Ideal für Studenten, Berufstätige mit Urlaub oder alle, die schnell mobil sein müssen.",
              "provider": { "@id": "https://www.roessnerfahrschule.de/#organization" },
              "timeRequired": "P4W",
              "educationalLevel": "Beginner"
            },
            "price": "2800",
            "priceCurrency": "EUR"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Course",
              "name": "BF17 - Begleitetes Fahren ab 17",
              "description": "Führerschein mit 17 Jahren mit Begleitperson. Früher Führerschein für mehr Fahrpraxis.",
              "provider": { "@id": "https://www.roessnerfahrschule.de/#organization" }
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "bestRating": "5",
        "worstRating": "1",
        "ratingCount": "127",
        "reviewCount": "127"
      },
      "review": [
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Sarah M." },
          "datePublished": "2024-11-15",
          "reviewBody": "Super Fahrschule! Habe meinen Führerschein beim ersten Versuch bestanden. Die Fahrlehrer sind geduldig und erklären alles verständlich.",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Mehmet K." },
          "datePublished": "2024-10-20",
          "reviewBody": "Intensivkurs in 3 Wochen gemacht und direkt bestanden. Sehr zu empfehlen!",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
        }
      ],
      "sameAs": [
        "https://www.facebook.com/fahrschuleroessner",
        "https://www.instagram.com/fahrschuleroessner",
        "https://g.page/fahrschule-roessner"
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+49-69-20326059",
          "contactType": "customer service",
          "availableLanguage": ["German", "English", "Turkish", "Russian"],
          "areaServed": "DE"
        }
      ]
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'organization-schema';
    
    // Remove existing
    const existing = document.getElementById('organization-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(scriptTag);

    return () => {
      const script = document.getElementById('organization-schema');
      if (script) script.remove();
    };
  }, []);
};

// FAQ Schema - for FAQ sections
export const useFAQSchema = (faqs) => {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'faq-schema';
    
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(scriptTag);

    return () => {
      const script = document.getElementById('faq-schema');
      if (script) script.remove();
    };
  }, [faqs]);
};

// Local Business Schema for specific location
export const useLocalBusinessSchema = (location) => {
  useEffect(() => {
    if (!location) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "DrivingSchool",
      "@id": `https://www.roessnerfahrschule.de/standorte/${location.slug}#localbusiness`,
      "name": `Fahrschule Rößner ${location.name}`,
      "description": location.meta_description || `Fahrschule in ${location.name}. Führerschein Klasse B, Intensivkurse und mehr.`,
      "url": `https://www.roessnerfahrschule.de/standorte/${location.slug}`,
      "telephone": location.phone || "+49 69 20 32 60 59",
      "email": location.email || "info@roessnerfahrschule.de",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": location.address?.split(',')[0] || location.address,
        "addressLocality": location.city || location.name,
        "postalCode": location.address?.match(/\d{5}/)?.[0] || "",
        "addressRegion": "Hessen",
        "addressCountry": "DE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": location.lat,
        "longitude": location.lng
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "10:00",
          "closes": "14:00"
        }
      ],
      "priceRange": "€€",
      "currenciesAccepted": "EUR",
      "paymentAccepted": "Cash, Credit Card, Bank Transfer",
      "image": "https://www.roessnerfahrschule.de/fahrschule.jpg",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5"
      },
      "parentOrganization": {
        "@id": "https://www.roessnerfahrschule.de/#organization"
      }
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'localbusiness-schema';
    
    const existing = document.getElementById('localbusiness-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(scriptTag);

    return () => {
      const script = document.getElementById('localbusiness-schema');
      if (script) script.remove();
    };
  }, [location]);
};

// Breadcrumb Schema
export const useBreadcrumbSchema = (items) => {
  useEffect(() => {
    if (!items || items.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'breadcrumb-schema';
    
    const existing = document.getElementById('breadcrumb-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(scriptTag);

    return () => {
      const script = document.getElementById('breadcrumb-schema');
      if (script) script.remove();
    };
  }, [items]);
};

// Common FAQs for driving school
export const commonFAQs = {
  general: [
    {
      question: "Was kostet der Führerschein Klasse B bei der Fahrschule Rößner?",
      answer: "Die Gesamtkosten für den Führerschein Klasse B liegen bei uns zwischen 2.300€ und 3.500€, abhängig von der Anzahl der benötigten Fahrstunden. Der Grundbetrag beträgt ca. 350€, eine Fahrstunde kostet ca. 55€. Nutzen Sie unseren Kostenrechner für eine genaue Schätzung."
    },
    {
      question: "Wie lange dauert die Führerscheinausbildung?",
      answer: "Die reguläre Führerscheinausbildung dauert durchschnittlich 3-6 Monate, je nach Ihrer Verfügbarkeit. Mit unserem Intensivkurs können Sie den Führerschein bereits in 2-4 Wochen machen."
    },
    {
      question: "Ab welchem Alter kann ich den Führerschein machen?",
      answer: "Sie können mit 17 Jahren den Führerschein mit begleitetem Fahren (BF17) machen oder mit 18 Jahren den regulären Führerschein Klasse B. Die Anmeldung ist bereits 6 Monate vor dem 17. bzw. 18. Geburtstag möglich."
    },
    {
      question: "Wie viele Fahrstunden brauche ich für den Führerschein?",
      answer: "Gesetzlich vorgeschrieben sind 12 Sonderfahrten: 5 Überlandfahrten, 4 Autobahnfahrten und 3 Nachtfahrten. Die Anzahl der Übungsfahrten ist individuell und hängt von Ihrem Lernfortschritt ab - im Durchschnitt benötigen unsere Fahrschüler 20-30 Fahrstunden insgesamt."
    },
    {
      question: "Was ist ein Führerschein-Intensivkurs?",
      answer: "Ein Intensivkurs ist eine komprimierte Führerscheinausbildung, bei der Sie täglich Theorie- und Praxisunterricht haben. So können Sie Ihren Führerschein in nur 2-4 Wochen machen - ideal für Studenten in den Semesterferien oder Berufstätige mit Urlaub."
    },
    {
      question: "Welche Standorte hat die Fahrschule Rößner?",
      answer: "Wir haben 4 Standorte: Frankfurt Schwanheim (Hauptstandort), Frankfurt Niederrad, Offenbach am Main und Bad Homburg vor der Höhe. Alle Standorte sind gut mit öffentlichen Verkehrsmitteln erreichbar."
    },
    {
      question: "Bietet die Fahrschule Rößner auch Theorieunterricht online an?",
      answer: "Wir bieten regelmäßigen Theorieunterricht an allen unseren Standorten an. Die Termine finden Sie in unserem Kalender. Zusätzlich empfehlen wir die Nutzung von Lern-Apps zur Vorbereitung auf die Theorieprüfung."
    },
    {
      question: "Wie hoch ist die Bestehensquote bei der Fahrschule Rößner?",
      answer: "Unsere Bestehensquote liegt bei über 95% - deutlich über dem Bundesdurchschnitt. Das verdanken wir unserer strukturierten Ausbildung und unseren erfahrenen Fahrlehrern mit über 34 Jahren Erfahrung."
    }
  ],
  intensivkurs: [
    {
      question: "Wie schnell kann ich den Führerschein mit einem Intensivkurs machen?",
      answer: "Mit unserem Intensivkurs können Sie den Führerschein in nur 2-4 Wochen machen. Die genaue Dauer hängt von Ihrem Lernfortschritt und der Verfügbarkeit von Prüfungsterminen ab."
    },
    {
      question: "Für wen ist ein Intensivkurs geeignet?",
      answer: "Ein Intensivkurs ist ideal für: Studenten in den Semesterferien, Berufstätige mit Urlaubszeit, Personen die schnell mobil sein müssen, oder alle die konzentriert und effizient lernen möchten."
    },
    {
      question: "Wie viel kostet ein Intensivkurs?",
      answer: "Ein Intensivkurs kostet in der Regel 200-400€ mehr als die reguläre Ausbildung, da die intensivere Betreuung und flexible Terminplanung mehr Aufwand erfordert. Die Gesamtkosten liegen bei ca. 2.800-3.800€."
    }
  ]
};

export default {
  useOrganizationSchema,
  useFAQSchema,
  useLocalBusinessSchema,
  useBreadcrumbSchema,
  commonFAQs
};
